import { FeedbackLine } from "../ui/FeedbackLine";
import { useState, type CSSProperties } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Box, Group, Paper, Skeleton, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import {
  useArtworkPins,
  useCoverlessScopes,
  useScheduleSearch,
  useSettings,
  useUpdateSetting,
  useWriteArtworkPin,
} from "../api/admin";
import { ApiError } from "../api/client";
import { scopeKey, type ArtworkPin, type CoverlessScope, type ScheduledEvent } from "../api/types";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import { Artwork } from "../ui/Artwork";
import { ArtworkPicker } from "../ui/ArtworkPicker";
import { Chip } from "../ui/Chip";

/** A timestamp column: the digits line up, and the date never wraps. */
const WHEN: CSSProperties = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/** One search result, and the default-cover row that reads like one. */
const RESULT: CSSProperties = { borderBottom: "1px solid var(--mantine-color-default-border)" };

/**
 * Cover pins, by scope rather than by airing.
 *
 * The rest of the UI pins from a segment, which needs an airing to start from. A series the
 * station scheduled and never broadcast has none, so its pin can only be made by naming the
 * scope — and until it airs, this is the only place the pin is visible at all.
 */

/** A scope a cover can be pinned to: one series, or one DJ billing. */
export interface PinScope {
  /** Dedupe key and React key: `show:19` or `dj:totalrage`. Matches a stored pin's scope. */
  key: string;
  show_id: number | null;
  /** The billing as typed; the server folds it. Null on a series scope. */
  djs: string | null;
  label: string;
  /** ISO start of the most recent airing seen for this scope. */
  last_seen: string;
  /** Airings of this scope with no cover. Absent on a scope that came from the search. */
  coverless?: number;
}

/**
 * Collapse a list of airings into the scopes a cover could be pinned to.
 *
 * The series id wins and the billing is the fallback, which is `scopeKey`'s rule and therefore
 * `scope_clause`'s; a row naming neither has no scope at all — the server answers 422 to one,
 * so offering it would be an invitation to a refusal. The API returns newest first, so the
 * first occurrence of a key is also the most recent and supplies `last_seen`.
 */
export function scopesFromEvents(events: ScheduledEvent[], t: TFunction): PinScope[] {
  const scopes: PinScope[] = [];
  const seen = new Set<string>();
  for (const event of events) {
    let scope: PinScope;
    if (event.show_id !== null) {
      scope = {
        key: scopeKey(event.show_id, null),
        show_id: event.show_id,
        djs: null,
        label:
          event.title?.trim() ||
          event.djs?.trim() ||
          t("artworkPins.seriesFallback", { id: event.show_id }),
        last_seen: event.t_start,
      };
    } else if (event.djs?.trim()) {
      const djs = event.djs.trim();
      scope = {
        key: scopeKey(null, djs),
        show_id: null,
        djs,
        label: djs,
        last_seen: event.t_start,
      };
    } else {
      continue;
    }
    if (seen.has(scope.key)) continue;
    seen.add(scope.key);
    scopes.push(scope);
  }
  return scopes;
}

/** A stored pin read back as the scope it was made against, for re-pinning or withdrawing. */
const scopeOf = (p: ArtworkPin, t: TFunction): PinScope => ({
  key: scopeKey(p.show_id, p.dj_key),
  show_id: p.show_id,
  // Null on a series pin. Otherwise the billing already folded, and folding a folded key is a
  // no-op, so it round-trips through the server unchanged.
  djs: p.show_id !== null ? null : p.dj_key,
  label:
    p.show_id !== null
      ? (p.show_title ?? t("artworkPins.seriesFallback", { id: p.show_id }))
      : (p.dj_key ?? ""),
  last_seen: p.pinned_at,
});

/**
 * A coverless scope as the scope a pin is written to.
 *
 * The raw billing is preferred over the folded key for the label; the key is what the write
 * and the dedupe use.
 */
export const scopeOfCoverless = (row: CoverlessScope, t: TFunction): PinScope => ({
  key: scopeKey(row.show_id, row.dj_key),
  show_id: row.show_id,
  djs: row.show_id !== null ? null : row.dj_key,
  label:
    row.show_id !== null
      ? (row.show_title ?? t("artworkPins.seriesFallback", { id: row.show_id }))
      : (row.djs ?? row.dj_key ?? ""),
  last_seen: row.last_seen,
  coverless: row.coverless,
});

export function ArtworkPins() {
  const [picking, setPicking] = useState<PinScope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("admin");

  const pins = useArtworkPins();
  const pin = useWriteArtworkPin();

  // The only place a pin is matched to a scope on this page: both the results marker and the
  // picker's current selection read from here. `scopeKey` folds a billing the way the server
  // does; they could only disagree on exotic casing, and a mismatch costs a marker, never a
  // wrong write — the write always sends `djs` as typed for the server to fold.
  const pinnedBy = new Map(
    (pins.data ?? []).map((p) => [scopeKey(p.show_id, p.dj_key), p.artwork_id]),
  );

  const write = async (scope: PinScope, artworkId: number | null) => {
    setError(null);
    try {
      // Exactly one scope key, which is what the server's validator insists on.
      await pin.mutateAsync({
        ...(scope.show_id !== null ? { show_id: scope.show_id } : { djs: scope.djs! }),
        artwork_id: artworkId,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  return (
    <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Title order={2} fz="lg" lh={1.5} mb={6}>
        {t("artworkPins.heading")}
      </Title>
      <FeedbackLine role="alert">{error}</FeedbackLine>

      <DefaultCover />
      <NeedPinSection pinnedBy={pinnedBy} onPick={setPicking} />

      <PinnedTable onPick={setPicking} onWithdraw={(scope) => void write(scope, null)} />

      {picking && (
        <ArtworkPicker
          current={pinnedBy.get(picking.key) ?? null}
          onClose={() => setPicking(null)}
          onPick={(artworkId) => {
            // The picker's Remove button hands back null, which here means withdraw.
            setPicking(null);
            void write(picking, artworkId);
          }}
        />
      )}
    </Paper>
  );
}

/**
 * What still needs a pin: the work list, or the captured schedule once a search is typed.
 */
function NeedPinSection({
  pinnedBy,
  onPick,
}: {
  pinnedBy: ReadonlyMap<string, number>;
  onPick: (scope: PinScope) => void;
}) {
  const [search, setSearch] = useState("");
  const zone = useZone();
  const { t } = useTranslation("admin");
  const needed = useCoverlessScopes();
  const schedule = useScheduleSearch(search);

  // Empty search: the work list, which is what the section is for. Typed: the captured
  // schedule, narrowed to scopes with no pin yet -- the only way to pin a series that has
  // never aired, and the one thing the coverless list cannot contain.
  const rows = search.trim()
    ? scopesFromEvents(schedule.data ?? [], t).filter((scope) => !pinnedBy.has(scope.key))
    : (needed.data ?? []).map((row) => scopeOfCoverless(row, t));

  return (
    <Stack component="section" gap="sm" mb={26} maw={900}>
      <Title
        order={3}
        fz="sm"
        lh={1.5}
        mb={4}
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        {/* Keyed on the work list rather than on the rows below, so typing a search does not
            clear a warning that is still true, and on the count rather than the row count,
            so a list holding only never-aired series does not claim anything is wearing the
            placeholder. Hidden from screen readers: the rows below state the same fact. */}
        {(needed.data ?? []).some((row) => row.coverless > 0) && (
          <IconAlertTriangle
            size={15}
            stroke={1.5}
            color="var(--mantine-color-warn-filled)"
            aria-hidden="true"
          />
        )}
        {t("artworkPins.needHeading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("artworkPins.needBlurb")}
      </Text>

      <Group gap={8} mb={10} fz={12} c="dimmed">
        <span>{t("artworkPins.searchLabel")}</span>
        <TextInput
          type="search"
          value={search}
          placeholder={t("artworkPins.searchPlaceholder")}
          aria-label={t("artworkPins.searchAria")}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Group>

      {rows.map((scope) => (
        <Group gap={8} py={6} style={RESULT} key={scope.key}>
          <span>{scope.label}</span>
          {/* Which of the two scopes this is. The label alone does not say. */}
          <Text component="span" fz="inherit" c="dimmed" ml={6}>
            {t(scope.show_id !== null ? "artworkPins.kindSeries" : "artworkPins.kindBilling")}
          </Text>
          <Box component="span" style={WHEN}>
            {msToZonedText(Date.parse(scope.last_seen), zone.name)}
          </Box>
          {/* Zero is a scope that has never aired, which the count would misreport as a
              tally of nothing. It says so in words instead. */}
          <Text component="span" fz="inherit" c="dimmed">
            {scope.coverless
              ? t("artworkPins.coverlessCount", { count: scope.coverless })
              : scope.coverless === 0
                ? t("artworkPins.neverAired")
                : null}
          </Text>
          <Chip onClick={() => onPick(scope)}>{t("artworkPins.pinCover")}</Chip>
        </Group>
      ))}
      {!rows.length && !needed.isLoading && !schedule.isLoading && (
        <Text fz={12} c="dimmed">
          {t(search.trim() ? "artworkPins.noMatches" : "artworkPins.noneNeedPin")}
        </Text>
      )}
    </Stack>
  );
}

/** The pins that exist, newest scope names and all, each with a way to change or withdraw it. */
function PinnedTable({
  onPick,
  onWithdraw,
}: {
  onPick: (scope: PinScope) => void;
  onWithdraw: (scope: PinScope) => void;
}) {
  const zone = useZone();
  const { t } = useTranslation("admin");
  const pins = useArtworkPins();

  return (
    <Stack component="section" gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("artworkPins.pinnedHeading")}
      </Title>
      {pins.isLoading ? (
        <Stack gap="xs">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={48} radius="sm" />
          ))}
        </Stack>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("artworkPins.colCover")}</Table.Th>
                <Table.Th>{t("artworkPins.colScope")}</Table.Th>
                <Table.Th>{t("artworkPins.colPinned")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(pins.data ?? []).map((p) => {
                const scope = scopeOf(p, t);
                return (
                  <Table.Tr key={scope.key}>
                    <Table.Td>
                      <Artwork src={p.url} seed={String(p.artwork_id)} size={48} alt="" />
                    </Table.Td>
                    <Table.Td>
                      {scope.label}
                      {/* Which of the two scopes this is. The label alone does not say. */}
                      <Text component="span" fz="inherit" c="dimmed" ml={6}>
                        {t(
                          p.show_id !== null ? "artworkPins.kindSeries" : "artworkPins.kindBilling",
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td style={WHEN}>
                      {msToZonedText(Date.parse(p.pinned_at), zone.name)}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Chip onClick={() => onPick(scope)}>{t("artworkPins.change")}</Chip>
                        <Chip variant="danger" onClick={() => onWithdraw(scope)}>
                          {t("artworkPins.withdraw")}
                        </Chip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {!pins.data?.length && (
                <Table.Tr>
                  <Table.Td colSpan={4}>{t("artworkPins.nonePinned")}</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}

/**
 * The cover a show wears when nothing else resolved one.
 *
 * A setting rather than a row in `artwork_pins`: that table scopes a pin to one series or
 * one billing and its check constraint insists on exactly one of them, which a fallback
 * covering everything is not. It is rendered here anyway, because this is the page where
 * covers are decided and a fallback nobody can find is a fallback nobody sets.
 */
function DefaultCover() {
  const { data, isLoading } = useSettings();
  const update = useUpdateSetting();
  const [picking, { open: pick, close: stopPicking }] = useDisclosure(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("admin");

  // `Number(undefined)` is NaN and `Number(0)` is 0, and `|| null` folds both to "none" --
  // which is what an unset setting and a cleared one both mean.
  const current =
    Number(data?.find((setting) => setting.key === "default_cover_artwork_id")?.value) || null;

  const write = async (artworkId: number | null) => {
    setError(null);
    try {
      // 0, not null: the setting is an int, and zero is the id no artwork has.
      await update.mutateAsync({ key: "default_cover_artwork_id", value: artworkId ?? 0 });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  return (
    <Stack component="section" gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("artworkPins.defaultHeading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("artworkPins.defaultBlurb")}
      </Text>
      <FeedbackLine role="alert">{error}</FeedbackLine>

      <Group gap={8} py={6} style={RESULT}>
        {current !== null ? (
          <Artwork src={`/api/artwork/${current}`} seed={String(current)} size={48} alt="" />
        ) : (
          <span>{t("artworkPins.defaultNone")}</span>
        )}
        <Chip disabled={isLoading || update.isPending} onClick={pick}>
          {current !== null ? t("artworkPins.change") : t("artworkPins.set")}
        </Chip>
        {current !== null && (
          <Chip variant="danger" onClick={() => void write(null)}>
            {t("artworkPins.clear")}
          </Chip>
        )}
      </Group>

      {picking && (
        <ArtworkPicker
          current={current}
          onClose={stopPicking}
          onPick={(artworkId) => {
            // The picker's Remove button hands back null, which here means "no default".
            stopPicking();
            void write(artworkId);
          }}
        />
      )}
    </Stack>
  );
}
